/**
 * Générateur — Il utilise le lien entre pourcentage d'évolution et coefficient
 * multiplicateur.
 * Attendu ID : 219 | Niveau : 3ème | Domaine : Proportionnalité (d16, O7)
 *
 * Source doc prof (OGD FONCTIONS 3e, séance B1S1). Plan de cartes auto-validé
 * 2026-07-16 (journal TODO_EXERCISEUR_3E.md §6) :
 *
 *   « coefficients »   — ±p % → × coef (l'automatisme fondateur)
 *   « evolutions »     — les 3 sens : finale / initiale / taux (rotation)
 *   « retour »         — retrouver le total depuis un pourcentage (élections)
 *   « nonsymetrie »    — l'imprimante : +x % à l'aller ≠ −x % au retour
 *   « sansplus »       — la calculatrice cassée : un SEUL produit/quotient
 *   « complementaire » — le DNB : le piège du pourcentage complémentaire
 *   « successives »    — évolutions successives = produit des coefficients
 *   « boite »          — « +50 % » = une fois et demie (QCM, confusion +/×)
 *
 * Écartés (signalé) : Ex.10 concours (paradoxe de Simpson, tableau lourd) et
 * Ex.13 salaires (le doc note « k en réponse libre, non corrigeable ») —
 * réservés au débat de classe. Valeurs EXACTES par construction (centimes).
 */

import {
    randomChoice, randInt,
    makeQcm, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 219;

const L_COEFFICIENTS = 'Passer de ±p % au coefficient multiplicateur';
const L_EVOLUTIONS = 'Calculer valeur finale, valeur initiale ou taux';
const L_RETOUR = 'Retrouver le total à partir d\'un pourcentage';
const L_NONSYMETRIE = 'L\'aller-retour en pourcentages n\'est pas symétrique';
const L_SANSPLUS = 'Un seul calcul : la calculatrice sans + ni −';
const L_COMPLEMENTAIRE = 'Le pourcentage complémentaire';
const L_SUCCESSIVES = 'Évolutions successives : multiplier les coefficients';
const L_BOITE = '« +50 % », c\'est multiplier par combien ?';

const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const frNb = (x) => String(x).replace('.', ',');
// SANS zéro final (« 4,6 », jamais « 4,60 ») : une saisie « 4,6 » doit matcher.
const centimesFr = (c) => {
    if (c % 100 === 0) return String(c / 100);
    if (c % 10 === 0) return `${Math.floor(c / 100)},${(c % 100) / 10}`;
    return `${Math.floor(c / 100)},${String(c % 100).padStart(2, '0')}`;
};

// ═════════ « coefficients » ═════════

const genCoefficientsTrous = () => {
    const p1 = randomChoice([4, 5, 7, 12, 15, 20, 30, 45, 60]);
    const p2 = randomChoice([3, 5, 8, 10, 15, 25, 35, 50]);
    const c1 = frNb((100 + p1) / 100);
    const c2 = frNb((100 - p2) / 100);
    const phrase = `Augmenter de ${p1} %, c'est multiplier par {{${c1}}} ; diminuer de ${p2} %, c'est multiplier par {{${c2}}}.`;
    const payload = makeTexteTrou(phrase,
        '<p><b>Complète avec les coefficients multiplicateurs.</b> Écris-les en écriture décimale.</p>',
        { ...opts(L_COEFFICIENTS), mode: 'saisie' });
    payload._meta.params = { p1, p2 };
    return payload;
};

// ═════════ « evolutions » — les 3 sens ═════════

const genEvolutionsTrous = () => {
    const sens = randomChoice(['finale', 'initiale', 'taux']);
    const hausse = randInt(0, 1) === 1;
    const p = randomChoice([5, 10, 12, 20, 24, 25, 30, 40, 50, 60]);
    const coefCent = hausse ? 100 + p : 100 - p;
    let phrase; let texte; let params;
    if (sens === 'finale') {
        const v0 = randomChoice([120, 150, 200, 240, 320, 450, 600, 750]);
        const v1 = (v0 * coefCent) / 100;
        if (!Number.isInteger(v1)) return genEvolutionsTrous.repli();
        phrase = `Valeur finale : {{${v1}}}.`;
        texte = `<p>Une valeur initiale de ${v0} ${hausse ? 'augmente' : 'diminue'} de ${p} %.</p>`
            + '<p><b>Calcule la valeur finale.</b></p>';
        params = { sens, v0, p, hausse, v1 };
    } else if (sens === 'initiale') {
        const v0 = randomChoice([250, 400, 500, 750, 800, 1200]);
        const v1 = (v0 * coefCent) / 100;
        if (!Number.isInteger(v1)) return genEvolutionsTrous.repli();
        phrase = `Valeur initiale : {{${v0}}}.`;
        texte = `<p>Après une ${hausse ? 'hausse' : 'baisse'} de ${p} %, une valeur atteint ${v1}.</p>`
            + '<p><b>Retrouve la valeur initiale.</b></p>';
        params = { sens, v0, p, hausse };
    } else {
        const v0 = randomChoice([200, 250, 400, 500, 800]);
        const v1 = (v0 * coefCent) / 100;
        if (!Number.isInteger(v1)) return genEvolutionsTrous.repli();
        phrase = `C'est une ${hausse ? 'hausse' : 'baisse'} de {{${p}}} %.`;
        texte = `<p>Une valeur passe de ${v0} à ${v1}.</p>`
            // « Calcule d'abord le coefficient » donnait la première étape.
            + '<p><b>Quel est le pourcentage d\'évolution ?</b></p>';
        params = { sens, v0, v1, p, hausse };
    }
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_EVOLUTIONS), mode: 'saisie' });
    payload._meta.params = params;
    return payload;
};
genEvolutionsTrous.repli = () => {
    const payload = makeTexteTrou('Valeur finale : {{130}}.',
        '<p>Une valeur initiale de 100 augmente de 30 %.</p>'
        + '<p><b>Calcule la valeur finale.</b></p>',
        { ...opts(L_EVOLUTIONS), mode: 'saisie' });
    payload._meta.params = { sens: 'repli' };
    return payload;
};

// ═════════ « retour » — élections ═════════

const genRetourTrous = () => {
    let pctMille; let total;
    for (let essai = 0; essai < 60; essai++) {
        pctMille = randomChoice([50, 85, 115, 125, 160, 205, 240, 325]);   // p en dixièmes de %
        total = randomChoice([12000, 15000, 20000, 40000, 60000, 84000]);
        if ((total * pctMille) % 1000 === 0) break;
        total = 0;
    }
    if (!total) { pctMille = 115; total = 20000; }
    const voix = (total * pctMille) / 1000;
    const pctAff = frNb(pctMille / 10);
    const phrase = `Nombre total de voix exprimées : {{${total}}}.`;
    const payload = makeTexteTrou(phrase,
        `<p>À une élection, une candidate a obtenu ${pctAff} % des voix exprimées, soit ${voix} voix.</p>`
        + '<p><b>Calcule le nombre total de voix exprimées.</b> Écris un entier, sans espaces.</p>',
        { ...opts(L_RETOUR), mode: 'saisie' });
    payload._meta.params = { pctMille, total, voix };
    return payload;
};

// ═════════ « nonsymetrie » — l'imprimante ═════════

const PAIRES_EXACTES = [[5, 8], [4, 5], [8, 10], [10, 16], [20, 25], [16, 20], [25, 40], [5, 4], [10, 8],
    [2, 5], [5, 2], [10, 25], [8, 5], [40, 50], [50, 40], [25, 20]];

const genNonSymetrieTrous = () => {
    let [a, b] = randomChoice(PAIRES_EXACTES);
    // % aller = 100·b/a ; % retour = 100·a/b — exacts par choix des paires.
    const aller = (100 * b) / a;
    const retour = (100 * a) / b;
    const allerAff = frNb(aller); const retourAff = frNb(retour);
    const machine = randomChoice(['Une photocopieuse', 'Une imprimante', 'Un logiciel de mise en page']);
    const phrase = `Pour passer de ${a} cm à ${b} cm : réglage {{${allerAff}}} % ; pour revenir de ${b} cm à ${a} cm : réglage {{${retourAff}}} %.`;
    const payload = makeTexteTrou(phrase,
        `<p>${machine} agrandit ou réduit selon un pourcentage. Un motif mesure ${a} cm ; `
        + `on veut l'imprimer à ${b} cm, puis retrouver ${a} cm à partir de la copie.</p>`
        // « les deux pourcentages ne sont PAS symétriques ! » était la
        // découverte visée par cette carte.
        + '<p><b>Complète les deux réglages.</b> Utilise une virgule si le résultat '
        + 'n\'est pas entier.</p>',
        { ...opts(L_NONSYMETRIE), mode: 'saisie' });
    payload._meta.params = { a, b, aller, retour };
    return payload;
};

// ═════════ « sansplus » — la calculatrice cassée ═════════

const genSansPlusTrous = () => {
    if (randInt(0, 1)) {
        // Consommation : v × (1 − p/100), au centième EXACT (boucle bornée).
        let v10 = 67; let p = 5; let resCent = 6365 / 10;
        for (let essai = 0; essai < 60; essai++) {
            const vv = randInt(45, 85); const pp = randomChoice([2, 4, 5, 6, 8, 10, 20]);
            const rr = vv * (100 - pp) / 10;
            if (Number.isInteger(rr)) { v10 = vv; p = pp; resCent = rr; break; }
        }
        const res = centimesFr(resCent);
        const phrase = `Le calcul : ${frNb(v10 / 10)} × {{${frNb((100 - p) / 100)}}} ; consommation : {{${res}}} L/100 km.`;
        const payload = makeTexteTrou(phrase,
            `<p>Ma voiture consomme ${p} % de MOINS que celle de mon voisin, qui consomme ${frNb(v10 / 10)} L/100 km. `
            + 'Ma calculatrice n\'a plus les touches + et − : je dois utiliser UNE multiplication.</p>'
            + '<p><b>Complète le coefficient puis le résultat.</b></p>',
            { ...opts(L_SANSPLUS), mode: 'saisie' });
        payload._meta.params = { famille: 'moins', v10, p };
        return payload;
    }
    // Prix avant hausse : v1 ÷ (1 + p/100) — construit depuis v0 exact (borné).
    let v0Cent = 140; let p = 3; let v1Cent = 144.2 * 1;
    for (let essai = 0; essai < 60; essai++) {
        const vv = randomChoice([120, 125, 140, 150, 160, 180, 200]);
        const pp = randomChoice([3, 5, 10, 20, 25]);
        const rr = vv * (100 + pp) / 100;
        if (Number.isInteger(rr)) { v0Cent = vv; p = pp; v1Cent = rr; break; }
    }
    if (!Number.isInteger(v1Cent)) { v0Cent = 140; p = 5; v1Cent = 147; } // repli sûr
    const phrase = `Le calcul : ${centimesFr(v1Cent)} ÷ {{${frNb((100 + p) / 100)}}} ; prix avant la hausse : {{${centimesFr(v0Cent)}}} €.`;
    const payload = makeTexteTrou(phrase,
        `<p>Le prix du litre d'essence a augmenté de ${p} % et coûte maintenant ${centimesFr(v1Cent)} €. `
        + 'Sans les touches + et −, je dois utiliser UNE division.</p>'
        + '<p><b>Complète le coefficient puis le prix avant la hausse.</b></p>',
        { ...opts(L_SANSPLUS), mode: 'saisie' });
    payload._meta.params = { famille: 'avant', v0Cent, p };
    return payload;
};

// ═════════ « complementaire » — le DNB ═════════

const genComplementaireTrous = () => {
    let n; let recus;
    for (let essai = 0; essai < 60; essai++) {
        n = randomChoice([200, 250, 400, 500]);
        recus = randInt(Math.round(n * 0.6), Math.round(n * 0.9));
        if (((n - recus) * 100) % n === 0) break;
        recus = 0;
    }
    if (!recus) { n = 200; recus = 156; }                  // 44/200 = 22 %
    const pct = ((n - recus) * 100) / n;
    const phrase = `Pourcentage d'élèves qui n'ont PAS obtenu le diplôme : {{${pct}}} %.`;
    const payload = makeTexteTrou(phrase,
        `<p>Sur ${n} élèves présentés au brevet, ${recus} ont été reçus.</p>`
        // « la question porte sur le complémentaire » désamorçait le piège.
        + '<p><b>Quel pourcentage des élèves n\'a PAS obtenu le diplôme ?</b></p>',
        { ...opts(L_COMPLEMENTAIRE), mode: 'saisie' });
    payload._meta.params = { n, recus, pct };
    return payload;
};

// ═════════ « successives » — produit des coefficients ═════════

const genSuccessivesTrous = () => {
    const fr = (x) => frNb(Math.round(x * 100) / 100);
    let C0 = 10000; let p = 2.5; let coefMil = 1025; let c1Mil = 10250; let c2Mil = 10506.25;
    for (let essai = 0; essai < 60; essai++) {
        const cc = randomChoice([1000, 2000, 4000, 5000, 8000, 10000, 20000]);
        const pp = randomChoice([2, 2.5, 4, 5, 10]);
        const cm = Math.round((100 + pp) * 10);            // coef × 1000
        const c1 = (cc * cm) / 1000;
        const c2 = (c1 * cm) / 1000;
        // Centimes EXACTS exigés pour les deux années.
        if (Math.round(c1 * 100) === c1 * 100 && Math.round(c2 * 100) === c2 * 100) {
            C0 = cc; p = pp; coefMil = cm; c1Mil = c1; c2Mil = c2; break;
        }
    }
    const phrase = `Après un an : {{${fr(c1Mil)}}} € ; après deux ans : {{${fr(c2Mil)}}} €.`;
    const payload = makeTexteTrou(phrase,
        `<p>On place ${C0} € à ${frNb(p)} % par an ; chaque année, les intérêts s'ajoutent au capital `
        // « le capital est donc MULTIPLIÉ par le même coefficient » et « ce
        // n'est pas deux fois les intérêts » donnaient la méthode ET le piège.
        + 'et produisent eux-mêmes des intérêts l\'année suivante.</p>'
        + '<p><b>Calcule le capital après un an, puis après deux ans.</b> '
        + 'Donne les centimes si besoin.</p>',
        { ...opts(L_SUCCESSIVES), mode: 'saisie' });
    payload._meta.params = { C0, p, coefMil };
    return payload;
};

// ═════════ « boite » — « +p % », c'est × combien ? (QCM) ═════════

const genBoiteQcm = () => {
    const p = randomChoice([50, 100, 200]);
    const chDemi = 'une fois et demie la quantité habituelle';
    const chDouble = 'le double de la quantité habituelle';
    const chTriple = 'le triple de la quantité habituelle';
    const correct = p === 50 ? chDemi : (p === 100 ? chDouble : chTriple);
    const produit = randomChoice(['céréales', 'jus de fruits', 'lessive', 'chocolats',
        'biscuits', 'pâte à tartiner', 'riz', 'compote']);
    const payload = makeQcm([chDemi, chDouble, chTriple], correct,
        `<p>Une boîte de ${produit} annonce : « ${p} % de produit GRATUIT en plus ».</p>`
        + '<p><b>La boîte contient donc…</b></p>',
        opts(L_BOITE));
    payload._meta.params = { p };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    coefficients_texte_trou_saisie: genCoefficientsTrous,
    evolutions_texte_trou_saisie: genEvolutionsTrous,
    retour_texte_trou_saisie: genRetourTrous,
    nonsymetrie_texte_trou_saisie: genNonSymetrieTrous,
    sansplus_texte_trou_saisie: genSansPlusTrous,
    complementaire_texte_trou_saisie: genComplementaireTrous,
    successives_texte_trou_saisie: genSuccessivesTrous,
    boite_qcm_unique: genBoiteQcm,
};

const FORMAT_LABELS = { texte_trou_saisie: 'Saisie trous', qcm_unique: 'QCM' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'coefficients', label: L_COEFFICIENTS, formatsList: buildFormats('coefficients') },
    { id: 'evolutions', label: L_EVOLUTIONS, formatsList: buildFormats('evolutions') },
    { id: 'retour', label: L_RETOUR, formatsList: buildFormats('retour') },
    { id: 'nonsymetrie', label: L_NONSYMETRIE, formatsList: buildFormats('nonsymetrie') },
    { id: 'sansplus', label: L_SANSPLUS, formatsList: buildFormats('sansplus') },
    { id: 'complementaire', label: L_COMPLEMENTAIRE, formatsList: buildFormats('complementaire') },
    { id: 'successives', label: L_SUCCESSIVES, formatsList: buildFormats('successives') },
    { id: 'boite', label: L_BOITE, formatsList: buildFormats('boite') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
